import React, { useReducer, createContext, useMemo } from 'react';
import { Action, TChildren } from '../typings/common';
import { extend } from '../utilities/helpers/common';
import { AuthStatus } from '../utilities/constants/common';

interface IState {
  userId: string;
  isUserSubscribed: boolean;
  freePremiumGenerationsAttemptsCount: number;
  isInitialUserDataUpdated: boolean;
  authStatus: AuthStatus;
  clientHash: string;
}

interface IContext {
  authState: IState;
  authDispatch: React.Dispatch<Action<ActionType>>;
}

interface Props {
  children: TChildren;
}

const initialState: IState = {
  userId: '',
  isUserSubscribed: false,
  freePremiumGenerationsAttemptsCount: 1,
  isInitialUserDataUpdated: false,
  authStatus: AuthStatus.PENDING,
  clientHash: '',
};

enum ActionType {
  SET_USER_ID = 'SET_USER_ID',
  SET_IS_USER_SUBSCRIBED = 'SET_IS_USER_SUBSCRIBED',
  SET_FREE_PREMIUM_GENERATIONS_ATTEMPTS_COUNT = 'SET_FREE_PREMIUM_GENERATIONS_ATTEMPTS_COUNT',
  SET_USER_DATA = 'SET_USER_DATA',
  SET_AUTH_STATUS = 'SET_AUTH_STATUS',
}

export const ActionCreator = {
  setUserId: (id: number | null) => ({
    type: ActionType.SET_USER_ID,
    payload: id,
  }),
  setIsUserSubscribed: (isSubscribed: boolean) => ({
    type: ActionType.SET_IS_USER_SUBSCRIBED,
    payload: isSubscribed,
  }),
  setFreePremiumGenerationsAttemptsCount: (attemptsCount: number) => ({
    type: ActionType.SET_FREE_PREMIUM_GENERATIONS_ATTEMPTS_COUNT,
    payload: attemptsCount,
  }),
  setUserData: (userData: Partial<IState>) => ({
    type: ActionType.SET_USER_DATA,
    payload: userData,
  }),
  setAuthStatus: (status: AuthStatus) => ({
    type: ActionType.SET_AUTH_STATUS,
    payload: status,
  }),
};

const reducer = (state: IState, action: Action<ActionType>) => {
  switch (action.type) {
    case ActionType.SET_USER_ID:
      return extend(state, {
        userId: action.payload,
      });
    case ActionType.SET_IS_USER_SUBSCRIBED:
      return extend(state, {
        isUserSubscribed: action.payload,
      });
    case ActionType.SET_FREE_PREMIUM_GENERATIONS_ATTEMPTS_COUNT:
      return extend(state, {
        freePremiumGenerationsAttemptsCount: action.payload,
      });
    case ActionType.SET_USER_DATA:
      return extend(state, action.payload);
    case ActionType.SET_AUTH_STATUS:
      return extend(state, {
        authStatus: action.payload,
      });
    default:
      return state;
  }
};

export const AuthContext = createContext<IContext>({
  authState: initialState,
  authDispatch: () => {},
});

const AuthProvider: React.FC<Props> = ({ children }) => {
  const [authState, authDispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => ({ authState, authDispatch }), [authState]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
